import React from "react";
import { trpc } from "../../main/components/trpc.ts";

interface NewUser {
  username: string;
  fullname: string;
  messagename: string;
  password: string;
  confirmPassword: string;
}

interface CreateUserProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const CreateUser = (props: CreateUserProps) => {
  const [newUser, setNewUser] = React.useState<NewUser>({
    username: "",
    fullname: "",
    messagename: "",
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewUser((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const utils = trpc.useContext();

  const submitMutation = trpc.user.createUser.useMutation({
    onMutate: () => {
      setIsSubmitting(true);
    },
    onError: () => {
      setIsSubmitting(false);
    },
    onSuccess: () => {
      utils.user.invalidate();
      setIsSubmitting(false);
      setNewUser({
        username: "",
        fullname: "",
        messagename: "",
        password: "",
        confirmPassword: "",
      });
      props.onSuccess();
    },
  });

  const handleSubmit = () => {
    submitMutation.mutate(newUser);
  };

  const validationItems = [
    {
      label: "Le username doit contenir au moins 4 caractères.",
      isValid: newUser.username.length >= 4,
    },
    {
      label: "Le nom complet doit contenir au moins 4 caractères.",
      isValid: newUser.fullname.length >= 4,
    },
    {
      label: "Le nom message doit contenir au moins 4 caractères.",
      isValid: newUser.messagename.length >= 4,
    },
    {
      label: "Le mot de passe doit contenir au moins 10 caractères.",
      isValid: newUser.password.length >= 10,
    },
    {
      label: "La confirmation du mot de passe doit correspondre.",
      isValid:
        newUser.password.length > 0 && newUser.password === newUser.confirmPassword,
    },
  ];

  const isFormValid = validationItems.every((item) => item.isValid);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div
        className="absolute inset-0"
        onClick={() => {
          if (!isSubmitting) {
            props.onCancel();
          }
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-title"
        className="relative z-10 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h2 id="create-user-title" className="text-xl font-semibold text-slate-900">
              Créer un utilisateur
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Créez un compte Cadendar avec un login, un nom affiché et un mot de passe.
            </p>
          </div>
          <button
            type="button"
            onClick={props.onCancel}
            disabled={isSubmitting}
            className="rounded-lg px-3 py-2 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Fermer
          </button>
        </div>

        <form
          className="mt-6 space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="username" className="block text-sm font-medium text-slate-700">
                Username
              </label>
              <input
                type="text"
                id="username"
                onChange={handleChange}
                value={newUser.username}
                autoFocus
                className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="fullname" className="block text-sm font-medium text-slate-700">
                Fullname
              </label>
              <input
                type="text"
                id="fullname"
                onChange={handleChange}
                value={newUser.fullname}
                className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="messagename"
                className="block text-sm font-medium text-slate-700"
              >
                Message Name
              </label>
              <input
                type="text"
                id="messagename"
                onChange={handleChange}
                value={newUser.messagename}
                className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
              <input
                type="password"
                id="password"
                onChange={handleChange}
                value={newUser.password}
                className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-slate-700"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                onChange={handleChange}
                value={newUser.confirmPassword}
                className="block w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          <div className="space-y-3 text-sm text-slate-600">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="font-medium text-slate-900">Validations requises avant la création</p>
              <ul className="mt-2 space-y-1.5">
                {validationItems.map((item) => (
                  <li
                    key={item.label}
                    className={item.isValid ? "text-green-700" : "text-red-700"}
                  >
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
            {!isFormValid ? (
              <p className="text-red-700">
                Le formulaire est incomplet ou invalide. Corrigez les points en rouge pour activer le bouton.
              </p>
            ) : null}
            {submitMutation.error ? (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-700">
                {submitMutation.error.message}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={props.onCancel}
              disabled={isSubmitting}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Création..." : "Créer l'utilisateur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUser;
